/**
 * Sidan för ett kundprojekt (/crm/kundprojekt/:id): grunddata, säkerhet,
 * parter med kontakter och leads, och knapparna "Lägg till part" och
 * "Skapa lead".
 *
 * Kundprojekt är kundens projekt som säljunderlag – inte Tikabs uppdrag
 * (/projects) eller referensprojekt.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import {
  Box, Container, Paper, Typography, Button, IconButton, Chip, Avatar, Divider, Tooltip, CircularProgress,
  MenuItem, Alert, Menu,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ConstructionIcon from '@mui/icons-material/Construction';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import LagbasLogo from '../components/LagbasLogo';
import KundprojektRedigering, { useSparatKvitto, KONFLIKT_TEXT } from '../modules/crm/KundprojektRedigering';
import { SakerhetsIkoner } from '../modules/crm/KundprojektVyer';
import {
  etikett, partSammanfattning, visaSakerhetsfalt, manadText, SAKERHET_VARNING,
  KP_TYPER, KP_BYGGSKEDEN, KP_ENTREPRENADFORMER, KP_STORLEKSKLASSER, KP_DRIFTKRAV, KP_STATUSAR, KP_KALLOR,
  JA_NEJ_OKANT, KP_IT_MILJOER, KP_PROJEKTKONTOR, KP_FILOVERFORING, KP_ROLLER,
} from '../modules/crm/kundprojekt';
import {
  laddaKundprojekt, sparaKundprojektAndringar, raderaKundprojekt, laddaLeadsMedIds,
} from '../modules/crm/kundprojektData';
import { skedesIndex, toMillis, lokalDatum } from '../modules/crm/pipelineFlode';
import { stageInfo } from '../modules/crm/skeden';

const lasKontakter = kundId => getDocs(query(collection(db, 'crmCompanies', kundId, 'contacts'), orderBy('name')))
  .then(s => s.docs.map(d => ({ id: d.id, ...d.data() })))
  .catch(() => []);

const initialer = namn => String(namn || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

function Falt({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{children || '–'}</Typography>
    </Box>
  );
}

// ── Sidan ───────────────────────────────────────────────────────────────────

export default function CRMKundprojekt() {
  const { kundprojektId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [kp, setKp] = useState(null);
  const [laddar, setLaddar] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [kontakter, setKontakter] = useState({}); // kundId → [kontakt]
  const [leads, setLeads] = useState([]);
  const [redigera, setRedigera] = useState(false);
  const [leadMeny, setLeadMeny] = useState(null);
  const [kvitto, visaKvitto] = useSparatKvitto();

  const ladda = useCallback(async () => {
    setLaddar(true);
    const [k, kunderSnap] = await Promise.all([
      laddaKundprojekt(kundprojektId),
      getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
    ]);
    setCompanies(kunderSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setKp(k);
    if (k) {
      const kundIds = [...new Set((k.parter || []).map(p => p.kundId))];
      const [kontaktListor, leadLista] = await Promise.all([
        Promise.all(kundIds.map(lasKontakter)),
        laddaLeadsMedIds((k.parter || []).flatMap(p => p.leadIds || [])),
      ]);
      setKontakter(Object.fromEntries(kundIds.map((id, i) => [id, kontaktListor[i]])));
      setLeads(leadLista);
    }
    setLaddar(false);
  }, [kundprojektId]);

  useEffect(() => { ladda(); }, [ladda]);

  const kundMap = useMemo(() => new Map(companies.map(c => [c.id, c])), [companies]);
  const leadMap = useMemo(() => Object.fromEntries(leads.map(l => [l.id, l])), [leads]);

  async function sparaParter(parter) {
    const r = await sparaKundprojektAndringar(kp, { ...kp, parter }, currentUser?.uid);
    visaKvitto(r.konflikt ? KONFLIKT_TEXT : 'Sparat', r.konflikt);
    await ladda();
  }

  async function taBortPart(index) {
    const p = kp.parter[index];
    if (kp.parter.length === 1) { alert('Ett kundprojekt behöver minst en part.'); return; }
    if (!window.confirm(`Ta bort ${kundMap.get(p.kundId)?.name || 'parten'} från kundprojektet? Kopplade kontakter och leads kopplas bort, men finns kvar.`)) return;
    await sparaParter(kp.parter.filter((_, i) => i !== index));
  }

  async function radera() {
    if (!window.confirm(`Radera kundprojektet "${kp.namn}"? Det tas bort från alla kunder, kontakter och leads.`)) return;
    await raderaKundprojekt(kp.id);
    navigate('/crm/leads');
  }

  function skapaLead(kundId) {
    setLeadMeny(null);
    navigate(`/crm/leads?new=1&companyId=${encodeURIComponent(kundId)}&kundprojektId=${encodeURIComponent(kp.id)}`);
  }

  const skede = l => {
    const s = stageInfo(l.status);
    return { index: skedesIndex(l.status), label: s.label };
  };

  const header = (
    <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
      <LagbasLogo />
      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
      <IconButton color="inherit" size="small" onClick={() => navigate(-1)} aria-label="Tillbaka"><ArrowBackIcon /></IconButton>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: '#aaa', display: 'block' }}>Affärer – Kundprojekt</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap>{kp?.namn || 'Kundprojekt'}</Typography>
          {kp && <SakerhetsIkoner kp={kp} storlek={17} />}
        </Box>
      </Box>
    </Box>
  );

  if (laddar && !kp) {
    return <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>{header}<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Box>;
  }
  if (!kp) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>{header}
        <Container maxWidth="md" sx={{ py: 4 }}><Alert severity="warning">Kundprojektet finns inte.</Alert></Container>
      </Box>
    );
  }

  const sammanfattning = partSammanfattning(kp, leadMap, skede);
  const sakerhet = visaSakerhetsfalt(kp);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {header}
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Grunddata */}
        <Paper sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ConstructionIcon sx={{ fontSize: 18, color: '#FF6B00' }} />
            <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>Kundprojekt</Typography>
            <Chip size="small" label={etikett(KP_STATUSAR, kp.status || 'aktivt')} />
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setRedigera(true)}
              sx={{ color: '#FF6B00', borderColor: '#FF6B00' }}>
              Redigera
            </Button>
          </Box>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
            <Falt label="Typ">{etikett(KP_TYPER, kp.typ)}</Falt>
            <Falt label="Ort">{kp.ort}</Falt>
            <Falt label="Byggskede">{etikett(KP_BYGGSKEDEN, kp.byggskede)}</Falt>
            <Falt label="Skedestart">{manadText(kp.skedeStart)}</Falt>
            <Falt label="Nästa skede">{etikett(KP_BYGGSKEDEN, kp.nastaSkede)}</Falt>
            <Falt label="Entreprenadform">{etikett(KP_ENTREPRENADFORMER, kp.entreprenadform)}</Falt>
            <Falt label="Storleksklass">{etikett(KP_STORLEKSKLASSER, kp.storleksklass)}</Falt>
            <Falt label="Driftkrav">{etikett(KP_DRIFTKRAV, kp.driftkrav)}</Falt>
            <Falt label="Verktyg i dag">{kp.verktygIdag}</Falt>
            <Falt label="Antal granskare">{kp.antalGranskare ?? ''}</Falt>
            <Falt label="Källa">{etikett(KP_KALLOR, kp.kalla)}</Falt>
            <Falt label="Registrerad">{toMillis(kp.skapad) ? lokalDatum(toMillis(kp.skapad)) : ''}</Falt>
          </Box>
          {kp.beskrivning && <Box sx={{ mt: 2 }}><Falt label="Beskrivning">{kp.beskrivning}</Falt></Box>}
        </Paper>

        {/* Säkerhet */}
        <Paper sx={{ p: 2.5, borderLeft: kp.sakerhetsskydd === 'ja' ? '4px solid #E65100' : undefined }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: sakerhet ? 2 : 0 }}>
            <ShieldOutlinedIcon sx={{ fontSize: 18, color: '#E65100' }} />
            <Typography variant="subtitle2" fontWeight="bold">Säkerhet</Typography>
            <Typography variant="body2" sx={{ ml: 1 }}>
              Säkerhetsskydd: <b>{etikett(JA_NEJ_OKANT, kp.sakerhetsskydd || 'okant')}</b>
            </Typography>
          </Box>
          {sakerhet && (
            <>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' } }}>
                <Falt label="IT-miljö">{etikett(KP_IT_MILJOER, kp.itMiljo)}</Falt>
                <Falt label="Projektkontor">{etikett(KP_PROJEKTKONTOR, kp.projektkontor)}</Falt>
                <Falt label="Säkerhetsskyddsavtal">{etikett(JA_NEJ_OKANT, kp.sakerhetsskyddsavtal)}</Falt>
                <Falt label="Externa parter behöver åtkomst">{etikett(JA_NEJ_OKANT, kp.externaParterBehoverAtkomst)}</Falt>
                <Falt label="Filöverföring">{etikett(KP_FILOVERFORING, kp.filoverforing)}</Falt>
                <Falt label="Granskning i dag">{kp.granskningIdag}</Falt>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#E65100' }}>{SAKERHET_VARNING}</Typography>
            </>
          )}
        </Paper>

        {/* Parter */}
        <Paper sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>Parter</Typography>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setRedigera(true)}
              sx={{ color: '#FF6B00', borderColor: '#FF6B00' }}>
              Lägg till part
            </Button>
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              onClick={e => (kp.parter.length === 1 ? skapaLead(kp.parter[0].kundId) : setLeadMeny(e.currentTarget))}
              sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
              Skapa lead
            </Button>
            <Menu anchorEl={leadMeny} open={!!leadMeny} onClose={() => setLeadMeny(null)}>
              {kp.parter.map((p, i) => (
                <MenuItem key={`${p.kundId}-${i}`} onClick={() => skapaLead(p.kundId)}>
                  Lead för {kundMap.get(p.kundId)?.name || 'kunden'}{p.roll ? ` (${etikett(KP_ROLLER, p.roll)})` : ''}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {sammanfattning.map(s => `${s.label}: ${s.text}`).join(' · ')}
          </Typography>

          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <Box component="thead">
                <Box component="tr" sx={{ bgcolor: '#f5f5f5' }}>
                  {['Kund', 'Roll', 'Kontakter', 'Leads', ''].map(h => (
                    <Box key={h} component="th" sx={{ px: 1.5, py: 1, textAlign: 'left', fontSize: '0.72rem', color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>{h}</Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {kp.parter.map((p, index) => {
                  const kund = kundMap.get(p.kundId);
                  const kundKontakter = new Map((kontakter[p.kundId] || []).map(k => [k.id, k]));
                  return (
                    <Box component="tr" key={`${p.kundId}-${index}`} sx={{ borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top' }}>
                      <Box component="td" sx={{ px: 1.5, py: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                          onClick={() => navigate(`/crm/companies/${p.kundId}`)}>
                          <Avatar src={kund?.logoUrl || undefined} variant="rounded"
                            sx={{ width: 30, height: 30, fontSize: '0.7rem', bgcolor: '#eee', color: '#666' }}>
                            {initialer(kund?.name)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={700} sx={{ '&:hover': { color: '#FF6B00' } }}>
                            {kund?.name || 'Okänd kund'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box component="td" sx={{ px: 1.5, py: 1.25 }}>{etikett(KP_ROLLER, p.roll) || '–'}</Box>
                      <Box component="td" sx={{ px: 1.5, py: 1.25 }}>
                        {(p.kontakter || []).length ? p.kontakter.map(k => (
                          <Typography key={k.kontaktId} variant="caption" sx={{ display: 'block' }}>
                            {kundKontakter.get(k.kontaktId)?.name || 'Borttagen kontakt'}
                            {k.rollIProjektet && <Box component="span" sx={{ color: 'text.secondary' }}> · {k.rollIProjektet}</Box>}
                          </Typography>
                        )) : <Typography variant="caption" color="text.secondary">Ingen kontakt</Typography>}
                      </Box>
                      <Box component="td" sx={{ px: 1.5, py: 1.25 }}>
                        {(p.leadIds || []).length ? p.leadIds.map(id => {
                          const l = leadMap[id];
                          if (!l) return <Typography key={id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>Borttaget lead</Typography>;
                          const s = stageInfo(l.status);
                          return (
                            <Box key={id} onClick={() => navigate(`/crm/leads/${id}`)}
                              sx={{ cursor: 'pointer', mb: 0.75, '&:hover .lead-titel': { color: '#FF6B00' } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography className="lead-titel" variant="body2" fontWeight={600}>{l.title}</Typography>
                                <Chip size="small" label={s.label} sx={{ height: 18, fontSize: '0.62rem', bgcolor: s.bg, color: s.color }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Nästa steg: {l.nextFollowUp ? `${l.nastaSteg || 'Uppföljning'} ${l.nextFollowUp}` : '–'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                Senaste aktivitet: {l.lastActivityDate ? `${l.lastActivityDate} ${String(l.lastActivitySummary || '').slice(0, 60)}` : '–'}
                              </Typography>
                            </Box>
                          );
                        }) : (
                          <Button size="small" startIcon={<AddIcon />} onClick={() => skapaLead(p.kundId)}
                            sx={{ color: '#FF6B00', textTransform: 'none', px: 0.5 }}>
                            Skapa lead
                          </Button>
                        )}
                      </Box>
                      <Box component="td" sx={{ px: 1, py: 1, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <Tooltip title="Redigera part">
                          <IconButton size="small" onClick={() => setRedigera(true)} aria-label="Redigera part">
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ta bort part">
                          <IconButton size="small" onClick={() => taBortPart(index)} aria-label="Ta bort part" sx={{ color: '#C62828' }}>
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" startIcon={<DeleteIcon />} onClick={radera} sx={{ color: '#C62828', textTransform: 'none' }}>
            Radera kundprojekt
          </Button>
        </Box>
      </Container>

      {redigera && (
        <KundprojektRedigering kundprojekt={kp} companies={companies} uid={currentUser?.uid}
          onClose={() => setRedigera(false)}
          onSparad={r => { visaKvitto(r.konflikt ? KONFLIKT_TEXT : 'Sparat', r.konflikt); ladda(); }} />
      )}
      {kvitto}
    </Box>
  );
}
